import { useState } from 'react';
import { useSelector } from 'react-redux';
import { IMenuItem, Modal, YesNo, Pagination, Dropdown, IconButton, MessageBox } from '../../components/Common';
import MemberPolicies from '../../components/MemberPolicies';
import { useHttpGetBranchesFromOrganizationOMX, useHttpGetOrganizationMembersOMX, useOrganizationId } from '../../hooks';
import { RootState } from '../../store';
import useHttpGetPoliciesByOrganizationOnMount from '../../hooks/organization/organizationApi/useHttpGetPoliciesByOrganizationOnMount';
import useHttpDeleteUser from '../../hooks/user/userApi/useHttpDeleteUser';
import { AppError, ErrorCodes, isErrorCode, getUserMessage } from '../../api/utils/ErrorHandler';
import InviteMembers from '../../components/InviteMembers';
import { OrganizationMembers } from '../../components/Members';
import SearchForm from '../../components/SearchForm';
import { OrganizationMembersWithBranchDataDto } from '../../commonTypes/acs';
import { useTranslation } from '../../hooks/useTranslation';
import { useSearchParams } from 'react-router-dom';
import { IMembersQueryParams } from '../../hooks/acs/acsApi/useHttpGetOrganizationMembersOMX';

const Members = () => {
    const content = useSelector((state: RootState) => state.language.data);
    const { t } = useTranslation('members');
    const organizationId = useOrganizationId();
    const [searchParams, setSearchParams] = useSearchParams();
    const [membersPerPage, setMembersPerPage] = useState<number>(10);

    const queryBranchId = searchParams.get('branchId') ?? undefined;
    const querySearch = searchParams.get('search') ?? undefined;
    const queryPage = searchParams.get('page') ?? undefined;
    const page = queryPage ? +queryPage - 1 : 0;

    const { policies } = useHttpGetPoliciesByOrganizationOnMount();

    const policyOptions: IMenuItem[] = policies.map(policy => ({ label: policy.leaveType, value: policy.id }));

    const defaultPolicy = policyOptions.find(policy => policy.label === 'Vacation') ?? policyOptions[0];
    const defaultPolicyId = defaultPolicy?.value.toString();

    const queryOrganizationPolicyId = searchParams.get('organizationPolicyId') ?? defaultPolicyId;

    const selectedPolicy: IMenuItem =
        policyOptions.length > 0
            ? policyOptions.find(policy => policy.value.toString() === queryOrganizationPolicyId) ?? defaultPolicy
            : { label: 'Vacation', value: -1 };

    if (defaultPolicyId && !searchParams.get('organizationPolicyId')) {
        setSearchParams(prevParams => {
            prevParams.set('organizationPolicyId', defaultPolicyId);
            return prevParams;
        });
    }

    const currentQuery: IMembersQueryParams = {
        page,
        organizationPolicyId: queryOrganizationPolicyId,
        branchId: queryBranchId,
        search: querySearch,
    };

    const { data, total, getMembers, setData, isReady } = useHttpGetOrganizationMembersOMX(organizationId, currentQuery, membersPerPage);
    const [openEditModal, setOpenEditModal] = useState(false);
    const [selectedMember, setSelectedMember] = useState<OrganizationMembersWithBranchDataDto>();
    const [openInviteModal, setOpenInviteModal] = useState(false);

    const { data: branches } = useHttpGetBranchesFromOrganizationOMX();
    const allBranchesOption = { value: -1, label: 'All branches' };
    const branchOptions: IMenuItem[] = [allBranchesOption, ...branches.map(branch => ({ value: branch.id, label: branch.name }))];

    const selectedBranch = branchOptions.find(item => item.value === queryBranchId) ?? branchOptions[0];

    const { exec: execDeleteUser } = useHttpDeleteUser();
    const [isDeleteModalVisible, setDeleteModalVisibility] = useState<boolean>(false);
    const [isAbortedModalVisible, setIsAbortedModalVisible] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState('');

    const deleteUser = () => {
        const userSub = selectedMember?.userSub;
        if (userSub) {
            execDeleteUser(userSub, {
                onSuccess: () => {
                    getMembers(currentQuery);
                    setDeleteModalVisibility(false);
                },
                onError: (error: AppError | string) => {
                    if (isErrorCode(error, ErrorCodes.USER_IS_PROJECT_MANAGER)) {
                        setErrorMessage(getUserMessage(error));
                        setIsAbortedModalVisible(true);
                        setDeleteModalVisibility(false);
                    }
                },
            });
        }
    };

    const onPageChange = (page: number) => {
        setSearchParams(
            prevParams => {
                prevParams.set('page', (page + 1).toString());
                return prevParams;
            },
            { replace: true }
        );

        getMembers({ ...currentQuery, page }).exec();
    };

    const handleSearch = (searchValue: string) => {
        setSearchParams(prevParams => {
            if (searchValue) prevParams.set('search', searchValue);
            else prevParams.delete('search');
            prevParams.set('page', '1');
            return prevParams;
        });

        const newQuery = { organizationPolicyId: queryOrganizationPolicyId, branchId: queryBranchId, page: 0 };
        if (searchValue)
            getMembers({
                ...newQuery,
                search: searchValue,
            }).exec();
        else getMembers(newQuery).exec();
    };

    const handleBranchChange = (branch: IMenuItem) => {
        setSearchParams(prevParams => {
            if (branch.value === -1) prevParams.delete('branchId');
            else prevParams.set('branchId', branch.value.toString());
            prevParams.set('page', '1');
            return prevParams;
        });

        const newQuery = { organizationPolicyId: queryOrganizationPolicyId, search: querySearch, page: 0 };
        if (branch.value !== -1) getMembers({ branchId: branch.value.toString(), ...newQuery }).exec();
        else getMembers(newQuery).exec();
    };

    const handlePolicyChange = (policy: IMenuItem) => {
        setSearchParams(prevParams => {
            if (policy.value === -1) prevParams.delete('organizationPolicyId');
            else prevParams.set('organizationPolicyId', policy.value.toString());
            prevParams.set('page', '1');
            return prevParams;
        });

        const newQuery = { branchId: queryBranchId, search: querySearch, page: 0 };
        if (policy.value !== -1) getMembers({ organizationPolicyId: policy.value.toString(), ...newQuery }).exec();
        else getMembers(newQuery).exec();
    };

    const handleMembersPerPageChange = (newMembersPerPage: number) => {
        setMembersPerPage(newMembersPerPage);
        setSearchParams(prevParams => {
            const newParams = new URLSearchParams(prevParams);
            newParams.set('page', '1');
            return newParams;
        });
        getMembers({ ...currentQuery, page: 0 }).exec();
    };

    return (
        <main className="members">
            <div className="members__content">
                <div className="members__header">
                    <div className="members__header--part">
                        <div className="members__select-wrapper">
                            <p className="paragraph-4 members__title">{`${content.members.policy}:`}</p>
                            <Dropdown className="members__select" menuItems={policyOptions} onChange={handlePolicyChange} selected={selectedPolicy} />
                        </div>
                        {branches && branches.length > 0 && (
                            <div className="members__select-wrapper">
                                <p className="paragraph-4 members__title">{`${content.common.branch}:`}</p>
                                <Dropdown
                                    className="members__select"
                                    menuItems={branchOptions}
                                    onChange={handleBranchChange}
                                    selected={selectedBranch}
                                />
                            </div>
                        )}
                    </div>
                    <div className="members__button-search">
                        <IconButton iconUrl="/svg/plus.svg" iconAlt="plus" onClick={() => setOpenInviteModal(true)} />
                        {data && <SearchForm action={handleSearch} initialSearchValue={querySearch} regexPattern={/^[\p{L}\p{N} ]*$/u} />}
                    </div>
                </div>
                {data && (
                    <OrganizationMembers
                        members={data}
                        openEditModal={() => setOpenEditModal(true)}
                        openDeleteModal={() => setDeleteModalVisibility(true)}
                        setSelectedMember={setSelectedMember}
                        selectedOrganizationPolicyId={queryOrganizationPolicyId}
                        isDataReady={isReady}
                    />
                )}
                <div className="members__pagination">
                    <Pagination
                        page={page}
                        total={total}
                        elementsPerPage={membersPerPage}
                        setElementsPerPage={handleMembersPerPageChange as React.Dispatch<React.SetStateAction<number>>}
                        onPageChange={onPageChange}
                    />
                </div>
            </div>
            <Modal isVisible={openEditModal} closeModal={() => setOpenEditModal(false)}>
                {selectedMember && <MemberPolicies selectedMember={selectedMember} close={() => setOpenEditModal(false)} setData={setData} />}
            </Modal>
            <Modal isVisible={openInviteModal} closeModal={() => setOpenInviteModal(false)}>
                <InviteMembers close={() => setOpenInviteModal(false)} />
            </Modal>
            <Modal isVisible={isDeleteModalVisible} closeModal={() => setDeleteModalVisibility(false)}>
                <YesNo
                    title={content.members.removeUserTitle}
                    description={content.members.removeUserDescription}
                    onNo={() => setDeleteModalVisibility(false)}
                    onYes={deleteUser}
                />
            </Modal>
            <Modal isVisible={isAbortedModalVisible} closeModal={() => setIsAbortedModalVisible(false)}>
                <MessageBox
                    title={t('removeUserAborted')}
                    description={`${errorMessage}\n\n${t('removeUserAbortedDescription')}`}
                    close={() => setIsAbortedModalVisible(false)}
                />
            </Modal>
        </main>
    );
};

export default Members;
